// An integer or decimal custom field's popover body on the Opportunities list. The rules are the record
// page's own (customFieldValueValidation), so the two surfaces cannot accept different input. Update is
// disabled while the value is unchanged or invalid, and an invalid value says why on the field rather
// than leaving a dead button. Enter in the box saves too. Saving an empty box clears the value.
import React, {useRef, useState} from 'react';
import {Button, EMPHASIS, TextField} from '@forecast-it/design-system';
import {
	isValidDecimal,
	isValidInteger,
	makeDecimalFilter,
	normaliseDecimal,
} from '../../../../shared/util/customFieldValueValidation';
import {Actions, Body} from './editorLayout';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

// What the input lets through at all: digits with an optional leading minus, plus for decimals either
// separator. A decimal is then filtered exactly as on the record page, keeping only the viewer's own
// separator, so in a '.' locale "1,500" becomes "1500". Partial values such as '-' or '1.' pass and are
// then reported as invalid, since they are on the way to a valid number.
const INTEGER_CHARACTERS = '^-?\\d*$';
const DECIMAL_CHARACTERS = '^[-\\d.,]*$';

export const NumberFieldEditor = ({
	node,
	def,
	current,
	onCommit,
	close,
	labels,
	decimalSeparator,
}: CustomFieldEditorProps): JSX.Element => {
	const isInteger = def.kind === 'INTEGER';
	// Stored decimals use '.'; the viewer reads and types their profile's separator.
	const usesComma = !isInteger && decimalSeparator === ',';
	const initial = usesComma ? (current ?? '').replace('.', ',') : current ?? '';
	const filterDecimal = makeDecimalFilter(decimalSeparator);
	const [draft, setDraft] = useState(initial);
	const unchanged = draft === initial;
	// The popover stays mounted through its exit animation, so a second press could otherwise save again.
	const saving = useRef(false);
	const valid = isInteger ? isValidInteger(draft) : isValidDecimal(draft);
	const save = () => {
		if (unchanged || !valid || saving.current) return;
		saving.current = true;
		const value = draft === '' ? null : usesComma ? normaliseDecimal(draft) : draft;
		saveCustomFieldValue({node, def, value, onCommit});
		close();
	};
	// The design-system TextField does not forward onKeyDown, so Enter is caught as it bubbles out of the
	// input instead. Only the input's own Enter saves: Enter on a focused button presses that button, and
	// Enter that confirms an IME composition is part of typing, not a save. save() keeps its own guards.
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.target instanceof HTMLInputElement && !e.nativeEvent.isComposing) save();
	};
	const invalidReason = isInteger ? labels.integerInvalid : labels.decimalInvalid;
	return (
		<Body data-cy="cf-number-editor" onKeyDown={onKeyDown}>
			<TextField
				data-cy="cf-number-input"
				label={def.displayName}
				value={draft}
				onValueChange={isInteger ? setDraft : (v: string) => setDraft(filterDecimal(v))}
				allowedCharactersRegex={isInteger ? INTEGER_CHARACTERS : DECIMAL_CHARACTERS}
				errorText={valid ? undefined : invalidReason}
			/>
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="cf-cancel" onPress={close}>
					{labels.cancel}
				</Button>
				<Button emphasis={EMPHASIS.HIGH} data-cy="cf-submit" disabled={unchanged || !valid} onPress={save}>
					{labels.update}
				</Button>
			</Actions>
		</Body>
	);
};

export default NumberFieldEditor;
